/**
 * Daily Conditions API Client for Admin Dashboard
 * Read-only monitoring of daily condition batch rows (docs/design/33-admin-daily-conditions.md)
 */

import apiClient from './client';

export type DailyConditionStatus = 'pending' | 'processing' | 'completed' | 'failed';
export type RiskFlag = 'green' | 'yellow' | 'red';

export interface DailyConditionDayStats {
  day: string; // UTC YYYY-MM-DD
  total: number;
  users: number;
  batches: number;
  completed: number;
  failed: number;
  pending: number;
  green: number;
  yellow: number;
  red: number;
  empty_request: number;
  with_recent_runs: number;
  needs_connection: number;
  guide_followed_set: number;
}

export interface DailyConditionStats {
  total: number;
  users: number;
  first_at: string | null;
  last_at: string | null;
  daily: DailyConditionDayStats[];
  errors: { code: string; count: number }[];
}

export interface DailyConditionListItem {
  id: string;
  user_id: string;
  user_name: string | null;
  batch_id: string;
  status: DailyConditionStatus;
  risk_flag: RiskFlag | null;
  daily_guide_type: string | null;
  message: string | null;
  error_code: string | null;
  empty_request: boolean;
  created_at: string;
  completed_at: string | null;
  duration_ms: number | null;
  guide_followed: boolean | null;
}

export interface DailyConditionListResponse {
  items: DailyConditionListItem[];
  pagination: { total: number; limit: number; offset: number; has_more: boolean };
}

export interface DailyConditionDetail extends DailyConditionListItem {
  context: {
    request?: Record<string, unknown>;
    user_profile?: Record<string, unknown>;
    memories?: unknown[];
    [key: string]: unknown;
  };
  result: Record<string, unknown> | null;
  error: Record<string, unknown> | null;
}

export interface DailyConditionTableRow {
  id: string;
  user_id: string;
  user_name: string | null;
  status: DailyConditionStatus;
  created_at: string;
  completed_at: string | null;
  /** Snapshot of user_info/device/custom as sent to the model ({ user, device, custom }) */
  user_profile: Record<string, unknown> | null;
  result: Record<string, unknown> | null;
  error_code: string | null;
}

export interface DailyConditionTable {
  day: string;
  offset: number;
  items: DailyConditionTableRow[];
  truncated: boolean;
}

export interface DailyConditionListParams {
  limit?: number;
  offset?: number;
  day?: string;
  status?: DailyConditionStatus;
  risk_flag?: RiskFlag;
  batch_id?: string;
  user?: string;
  empty_request?: boolean;
}

export const dailyConditionsApi = {
  async getStats(days = 14): Promise<DailyConditionStats> {
    const response = await apiClient.get<DailyConditionStats>('/daily-conditions/stats', { params: { days } });
    return response.data;
  },

  async list(params: DailyConditionListParams): Promise<DailyConditionListResponse> {
    const response = await apiClient.get<DailyConditionListResponse>('/daily-conditions', { params });
    return response.data;
  },

  async getTable(day: string, offset = 0): Promise<DailyConditionTable> {
    const response = await apiClient.get<DailyConditionTable>('/daily-conditions/table', {
      params: { day, offset },
    });
    return response.data;
  },

  /** Every row of one day, paging past the per-request cap. */
  async getAllRowsOfDay(day: string): Promise<DailyConditionTableRow[]> {
    const rows: DailyConditionTableRow[] = [];
    for (;;) {
      const page = await this.getTable(day, rows.length);
      rows.push(...page.items);
      if (!page.truncated || page.items.length === 0) return rows;
    }
  },

  async get(id: string): Promise<DailyConditionDetail> {
    const response = await apiClient.get<DailyConditionDetail>(`/daily-conditions/${id}`);
    return response.data;
  },
};
